import { SettingsCard } from '@/components/settings/SettingsCard';
import { settingsCardStyle, settingsHeadingStyle, settingsDescriptionStyle, settingsButtonStyle, settingsPrimaryButtonStyle } from '@/components/settings/settingsStyles';
import { ResponsiveTable } from '@/components/shared/ResponsiveTable';
import { tabKeyboard } from '@/lib/tabKeyboard';
import { MovableDialog } from '@/components/shared/MovableDialog';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  v2,
  ApiError,
  parseApiErrorBody,
  type MergedSetting,
  type MergedSettingsMap,
  type SettingsScope,
  type SettingsScopeRow,
  type SettingsWriteBody,
} from '@/api/v2Client';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { Spinner } from '@/components/dashboard/Spinner';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { PageToast, type ToastMessage } from '@/components/shared/PageToast';
import { useUiToast } from '@/hooks/useUiToast';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { formatDate } from '@/lib/format';
// ----- Phase 5.X-settings-schema-ui — schema + components -----
import {
  SETTINGS_SCHEMA,
  SUBMISSION_CAPABLE_KEYS,
  SECTION_ORDER,
  groupSchemaBySection,
} from '@/lib/settings-schema';
import { SettingsSectionPanel, SettingsSectionFields } from '@/components/settings/SettingsSectionPanel';
import { AssistantModelCatalogPanel } from '@/components/settings/AssistantModelCatalogPanel';
import {
  CurrentConfigurationPanel,
  type EffectiveScope,
  type EffectiveValueInfo,
} from '@/components/settings/CurrentConfigurationPanel';
// Phase notification-preferences-and-events (TODO-099)
import { NotificationPreferencesPanel } from '@/components/settings/NotificationPreferencesPanel';
// navigation-completion Stage C — organisation navigation policy panel.
import { NavigationPolicyPanel } from '@/components/settings/NavigationPolicyPanel';

// Phase 5.20b — Settings UI write/edit flow.
//
// Builds on Phase 5.20a's read-only scaffold. Adds:
//   - inline SettingEditModal (create + edit, matches D-2-polish's
//     inlined-helpers pattern: each admin page keeps its own copy
//     until rule-of-three forces shared extraction);
//   - per-row Edit + Delete buttons, RBAC-gated per scope;
//   - "+ New setting" button at the top of each section;
//   - PageToast feedback on success; parseApiErrorBody on 4xx;
//   - OrgLocked behaviour: when an org row is locked, the same-Key
//     row on the user scope disables Edit and explains why (see
//     ORG_LOCKED_TOOLTIP) rather than silently failing on PUT.
//
// RBAC matrix (mirrors src/routes/settings.ts):
//   - Org PUT/DELETE   → Users:manage
//   - User PUT/DELETE  → auth-only (no Resource permission)
//   - Submission PUT/DELETE → Submissions:write (checked here; the
//     backend additionally requires the user to be able to write
//     the *specific* submission, so 403s are still possible).

const ORG_LOCKED_TOOLTIP =
  'Org-locked — only org admins can override this key.';

const submissionIdInputStyle: CSSProperties = {
  width: 'min(360px, 100%)',
  padding: '6px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
};

const loadButtonStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const rowActionButtonStyle: CSSProperties = {
  ...settingsButtonStyle,
  padding: '4px 10px',
  marginRight: 4,
};

const rowActionButtonDisabledStyle: CSSProperties = {
  ...rowActionButtonStyle,
  color: 'var(--text-muted)',
  cursor: 'not-allowed',
};

const newButtonStyle = settingsPrimaryButtonStyle();

const tdStyle: CSSProperties = { padding: '10px 8px', verticalAlign: 'top' };

/**
 * Render a `Value` of unknown shape as a stable, compact string. The
 * backend JSON-decodes server-side, so `Value` could be any primitive,
 * an array, or an object. Strings render bare; everything else gets
 * `JSON.stringify`d with `null, 2` truncated for tabular display.
 */
function renderValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 200 ? `${s.slice(0, 197)}…` : s;
  } catch {
    return String(v);
  }
}

/** Serialise a Value for the textarea — primitives stay readable, everything else uses pretty JSON. */
function valueToFieldText(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v === null || v === undefined) return '';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

/**
 * Parse the textarea string back into a JSON value. The user can type
 * either a bare string or any JSON literal. Empty input is rejected;
 * the backend uses absence (i.e. DELETE) for that.
 */
function parseFieldText(raw: string): { ok: true; value: unknown } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: false, error: 'Value is required' };
  try {
    return { ok: true, value: JSON.parse(trimmed) };
  } catch {
    // Fall back to a literal string — useful for the common case where
    // the user just typed `production` instead of `"production"`.
    return { ok: true, value: trimmed };
  }
}

// Phase E-polish-extract-3 — inlined ConfirmModal hoisted to
// `@/components/shared/ConfirmModal`. PageToast was hoisted in
// Phase E-polish-extract-2.

// ----- Inline SettingEditModal ----- //
//
// One modal serves both create and edit. `setting === null` is create
// mode (Key input enabled). When editing, Key is disabled — the backend
// PUT endpoint takes the Key in the body, so renaming would mean a
// delete + create, which we intentionally don't support from the UI.

interface SettingEditModalProps {
  readonly scope: SettingsScope;
  readonly setting: SettingsScopeRow | null;
  readonly submissionId?: string;
  readonly onClose: () => void;
  readonly onSaved: (row: SettingsScopeRow) => void;
}

function SettingEditModal({
  scope,
  setting,
  submissionId,
  onClose,
  onSaved,
}: SettingEditModalProps): ReactElement {
  const isEdit = setting !== null;
  const [keyText, setKeyText] = useState(setting?.Key ?? '');
  const [valueText, setValueText] = useState(() => valueToFieldText(setting?.Value));
  const [orgLocked, setOrgLocked] = useState<boolean>(setting?.OrgLocked ?? false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);

    const trimmedKey = keyText.trim();
    if (!trimmedKey) {
      setError('Key is required');
      return;
    }
    const parsed = parseFieldText(valueText);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }

    // T-17 (D6) — only submission-capable keys can be set per submission. The
    // server refuses the rest (400 SettingScopeNotAllowed); this just says so
    // before the round trip.
    if (scope === 'submission' && !isEdit && !SUBMISSION_CAPABLE_KEYS.includes(trimmedKey)) {
      setError(
        `"${trimmedKey}" cannot be set per submission. Submission-level settings: ${SUBMISSION_CAPABLE_KEYS.join(', ')}.`,
      );
      return;
    }

    const body: SettingsWriteBody = { key: trimmedKey, value: parsed.value };
    if (scope === 'org') body.orgLocked = orgLocked;

    setSaving(true);
    try {
      let updated: SettingsScopeRow;
      if (scope === 'org') {
        updated = await v2.settings.updateOrg(body);
      } else if (scope === 'user') {
        updated = await v2.settings.updateUser(body);
      } else {
        if (!submissionId) throw new Error('submissionId is required for submission scope');
        updated = await v2.settings.updateSubmission(submissionId, body);
      }
      onSaved(updated);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (parseApiErrorBody(err)?.message ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Save failed';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const scopeLabel =
    scope === 'org' ? 'org' : scope === 'user' ? 'user' : 'submission';

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="settings.editor"
      title={isEdit ? `Edit "${setting?.Key}"` : `New ${scopeLabel} setting`}
      ariaLabel={isEdit ? `Edit ${scopeLabel} setting` : `New ${scopeLabel} setting`}
      onClose={onClose}
      closeOnBackdrop
      width="min(560px, 92vw)"
      maxHeight="88vh"
      zIndex={300}
      headerRight={
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}
        >
          ×
        </button>
      }
    >
      <form onSubmit={handleSubmit}>


        {error && (
          <div
            role="alert"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg)',
              padding: '8px 12px',
              borderRadius: 4,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            Key {isEdit ? '(read-only)' : '*'}
          </span>
          <input
            type="text"
            value={keyText}
            onChange={(e) => setKeyText(e.target.value)}
            disabled={isEdit}
            aria-label="Setting key"
            list={scope === 'submission' && !isEdit ? 'submission-capable-setting-keys' : undefined}
            required
            style={{
              width: '100%',
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              background: isEdit ? 'var(--surface2)' : 'var(--surface)',
              color: isEdit ? 'var(--text-muted)' : 'var(--text)',
            }}
          />
        </label>

        {scope === 'submission' && !isEdit && (
          <datalist id="submission-capable-setting-keys">
            {SUBMISSION_CAPABLE_KEYS.map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
        )}

        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            Value (JSON or bare string) *
          </span>
          <textarea
            value={valueText}
            onChange={(e) => setValueText(e.target.value)}
            aria-label="Setting value"
            rows={8}
            required
            style={{
              width: '100%',
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 13,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              resize: 'vertical',
            }}
          />
        </label>

        {scope === 'org' && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={orgLocked}
              onChange={(e) => setOrgLocked(e.target.checked)}
              aria-label="Org-locked"
            />
            <span>
              Org-locked — when set, users and submissions cannot override this key.
            </span>
          </label>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </MovableDialog>
  );
}

// ----- Scope section ----- //

interface ScopeSectionState {
  rows: readonly SettingsScopeRow[] | null;
  error: string | null;
  reloadKey: number;
}

function emptyScopeState(): ScopeSectionState {
  return { rows: null, error: null, reloadKey: 0 };
}

interface ScopeSectionProps {
  readonly heading: string;
  readonly subtext: string;
  readonly state: ScopeSectionState;
  readonly emptyText: string;
  readonly emptyIcon?: string;
  readonly onRetry: () => void;
  /** Rendered above the table when we have rows — e.g. a submission ID picker. */
  readonly controlSlot?: ReactElement;
  /** Whether the current user is allowed to write rows in this scope. */
  readonly canWrite: boolean;
  /** Called when the user clicks "+ New setting". */
  readonly onNew: () => void;
  /** Called when the user clicks Edit on a row. */
  readonly onEdit: (row: SettingsScopeRow) => void;
  /** Called when the user clicks Delete on a row. */
  readonly onDelete: (row: SettingsScopeRow) => void;
  /**
   * Keys that are locked at the org level. Only meaningful on the user
   * scope: rows whose Key is in this set have Edit/Delete disabled
   * with `ORG_LOCKED_TOOLTIP` as the explanation.
   */
  readonly lockedOrgKeys?: ReadonlySet<string>;
}

function ScopeSection({
  heading,
  subtext,
  state,
  emptyText,
  emptyIcon,
  onRetry,
  controlSlot,
  canWrite,
  onNew,
  onEdit,
  onDelete,
  lockedOrgKeys,
}: ScopeSectionProps): ReactElement {
  return (
    <SettingsCard
      title={heading}
      description={subtext}
      ariaLabel={`${heading} section`}
      actions={canWrite && <button type="button" onClick={onNew} style={newButtonStyle}>+ New setting</button>}
    >

      {controlSlot}

      {state.error && (
        <div
          role="alert"
          style={{
            color: 'var(--red)',
            background: 'var(--red-bg)',
            padding: '8px 12px',
            borderRadius: 4,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          {state.error}
          <button
            type="button"
            onClick={onRetry}
            style={{ marginLeft: 12, padding: '2px 10px', cursor: 'pointer', fontSize: 12 }}
          >
            Retry
          </button>
        </div>
      )}

      {state.rows === null && !state.error && <Spinner caption={`Loading ${heading.toLowerCase()}…`} />}

      {state.rows !== null && state.rows.length === 0 && !state.error && (
        <EmptyState icon={emptyIcon ?? '⚙'} heading={emptyText} />
      )}

      {state.rows !== null && state.rows.length > 0 && (
        <ResponsiveTable style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
              <th style={tdStyle}>Key</th>
              <th style={tdStyle}>Value</th>
              <th style={{ ...tdStyle, textAlign: 'center' }}>Org-locked</th>
              <th style={tdStyle}>Updated</th>
              <th style={tdStyle}>Updated by</th>
              <th style={{ ...tdStyle, width: 160 }}></th>
            </tr>
          </thead>
          <tbody>
            {state.rows.map((row) => {
              const lockedByOrg = lockedOrgKeys?.has(row.Key) ?? false;
              const editDisabled = lockedByOrg;
              const deleteDisabled = lockedByOrg;
              const lockedTitle = lockedByOrg ? ORG_LOCKED_TOOLTIP : undefined;
              return (
                <tr
                  key={row.Key}
                  style={{ borderBottom: '1px solid var(--border)' }}
                  data-testid={`settings-row-${row.Key}`}
                >
                  <td
                    style={{
                      ...tdStyle,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {row.Key}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      maxWidth: 480,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={renderValue(row.Value)}
                  >
                    {renderValue(row.Value)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontSize: 14 }}>
                    {row.OrgLocked ? (
                      <span style={{ color: 'var(--accent)' }} aria-label="Org-locked">
                        🔒
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)' }} aria-label="Not locked">
                        —
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}>
                    {formatDate(row.UpdatedAt)}
                  </td>
                  <td style={{ ...tdStyle, fontSize: 13, color: 'var(--text-muted)' }}>
                    {row.UpdatedBy ?? '—'}
                  </td>
                  <td style={tdStyle}>
                    {canWrite && (
                      <>
                        <button
                          type="button"
                          onClick={() => onEdit(row)}
                          disabled={editDisabled}
                          aria-label={`Edit ${row.Key}`}
                          title={lockedTitle}
                          style={editDisabled ? rowActionButtonDisabledStyle : rowActionButtonStyle}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(row)}
                          disabled={deleteDisabled}
                          aria-label={`Delete ${row.Key}`}
                          title={lockedTitle}
                          style={deleteDisabled ? rowActionButtonDisabledStyle : rowActionButtonStyle}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ResponsiveTable>
      )}
    </SettingsCard>
  );
}

// ----- Page ----- //

interface EditModalTarget {
  readonly scope: SettingsScope;
  readonly setting: SettingsScopeRow | null;
}

interface DeleteTarget {
  readonly scope: SettingsScope;
  readonly key: string;
}

// ----- Phase 5.20c — Effective view tab ----- //

type SettingsView = 'raw' | 'effective';

/**
 * Cached effective-view fetch result + the submission id we fetched against
 * (null = `listAll()` / org+user merge only). Refetch triggers when:
 *   1. tab is flipped to 'effective' for the first time (rows === null), OR
 *   2. the loaded submission id changes and we were previously fetched against
 *      a different id (or against `null`).
 */
interface EffectiveState {
  rows: MergedSettingsMap | null;
  error: string | null;
  reloadKey: number;
  fetchedForSubmissionId: string | null;
}

const emptyEffectiveState: EffectiveState = {
  rows: null,
  error: null,
  reloadKey: 0,
  fetchedForSubmissionId: null,
};

const tabButtonStyle: CSSProperties = {
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const tabButtonActiveStyle: CSSProperties = {
  ...tabButtonStyle,
  background: 'var(--accent)',
  color: '#fff',
  border: '1px solid var(--accent)',
};

const sourcePillBase: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const sourcePillStyles: Readonly<Record<SettingsScope, CSSProperties>> = Object.freeze({
  org: { ...sourcePillBase, background: '#EFF6FF', color: '#1d4ed8', border: '1px solid #BFDBFE' },
  user: { ...sourcePillBase, background: '#ECFDF5', color: '#047857', border: '1px solid #6EE7B7' },
  submission: {
    ...sourcePillBase,
    background: '#F5F3FF',
    color: '#6D28D9',
    border: '1px solid #DDD6FE',
  },
});

interface EffectiveViewProps {
  readonly state: EffectiveState;
  readonly loadedSubmissionId: string | null;
  readonly onRetry: () => void;
  readonly showToast: (msg: ToastMessage) => void;
}

function EffectiveView({
  state,
  loadedSubmissionId,
  onRetry,
  showToast,
}: EffectiveViewProps): ReactElement {
  const entries = useMemo(() => {
    if (!state.rows) return [];
    return Object.entries(state.rows).sort(([a], [b]) => a.localeCompare(b));
  }, [state.rows]);

  const handleCopyEffectiveJson = async (): Promise<void> => {
    if (!state.rows) return;
    const text = JSON.stringify(state.rows, null, 2);
    const keyCount = Object.keys(state.rows).length;
    const successMsg = `Copied ${keyCount} key${keyCount === 1 ? '' : 's'} to clipboard`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        showToast({ type: 'success', text: successMsg });
        return;
      }
      // Fallback for non-secure contexts (older browsers, http://).
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        const ok = document.execCommand('copy');
        if (!ok) throw new Error('execCommand returned false');
        showToast({ type: 'success', text: successMsg });
      } finally {
        document.body.removeChild(textarea);
      }
    } catch (err) {
      showToast({
        type: 'error',
        text: `Copy failed: ${err instanceof Error ? err.message : 'unknown error'}`,
      });
    }
  };

  const handleDownloadEffectiveJson = (): void => {
    if (!state.rows) return;
    const text = JSON.stringify(state.rows, null, 2);
    const keyCount = Object.keys(state.rows).length;
    const scope = loadedSubmissionId
      ? `submission-${loadedSubmissionId.slice(0, 8)}`
      : 'org-user';
    // Replace `:` and `.` so Windows accepts the filename.
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `effective-settings-${scope}-${timestamp}.json`;

    try {
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Defer revoke — some browsers cancel the download if the URL is
      // revoked synchronously after click.
      globalThis.setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast({
        type: 'success',
        text: `Downloaded ${keyCount} key${keyCount === 1 ? '' : 's'} as ${filename}`,
      });
    } catch (err) {
      showToast({
        type: 'error',
        text: `Download failed: ${err instanceof Error ? err.message : 'unknown error'}`,
      });
    }
  };

  return (
    <section data-testid="effective-view" style={{ marginTop: 16 }}>
      <p style={{ margin: '0 0 12px 0', fontSize: 13, color: 'var(--text-muted)' }}>
        Read-only view of resolved settings (Phase 5.9 3-tier merge: org &rarr; user
        &rarr; submission). To modify, switch back to <strong>Raw rows</strong>.
      </p>
      <p style={{ margin: '0 0 16px 0', fontSize: 12, color: 'var(--text-muted)' }}>
        {loadedSubmissionId ? (
          <>
            Showing merged settings for submission <code>{loadedSubmissionId}</code>. To
            change scope, load a different submission ID (or clear it) on the Raw rows
            tab.
          </>
        ) : (
          <>
            Showing org &rarr; user merge only. Load a submission ID on the Raw rows tab
            to include submission-level overrides.
          </>
        )}
      </p>

      {state.rows !== null && !state.error && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => void handleCopyEffectiveJson()}
            style={rowActionButtonStyle}
            data-testid="copy-effective-json-btn"
          >
            📋 Copy as JSON
          </button>
          <button
            type="button"
            onClick={handleDownloadEffectiveJson}
            style={rowActionButtonStyle}
            data-testid="download-effective-json-btn"
          >
            💾 Download as JSON
          </button>
          <span style={{ marginLeft: 4, opacity: 0.7, fontSize: 12 }} data-testid="copy-effective-json-keycount">
            {Object.keys(state.rows).length} key{Object.keys(state.rows).length === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {state.error ? (
        <div
          role="alert"
          style={{
            background: 'var(--red-bg)',
            border: '1px solid #FECACA',
            borderRadius: 6,
            padding: '12px 16px',
            color: 'var(--red)',
            fontSize: 13,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{state.error}</span>
          <button
            type="button"
            onClick={onRetry}
            style={{
              ...rowActionButtonStyle,
              background: 'var(--surface)',
              color: 'var(--text)',
            }}
          >
            Retry
          </button>
        </div>
      ) : state.rows === null ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spinner />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon="🔧"
          heading="No effective settings"
          subtext="No org, user, or submission settings resolved for this view."
        />
      ) : (
        <ResponsiveTable style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
              <th style={{ padding: '10px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                Key
              </th>
              <th style={{ padding: '10px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                Value
              </th>
              <th style={{ padding: '10px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                Source
              </th>
              <th style={{ padding: '10px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                Locked
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, merged]: [string, MergedSetting]) => (
              <tr key={key} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={tdStyle}>
                  <code style={{ fontSize: 12 }}>{key}</code>
                </td>
                <td style={tdStyle}>
                  <code
                    style={{ fontSize: 12, color: 'var(--text)' }}
                    data-testid={`effective-value-${key}`}
                  >
                    {renderValue(merged.Value)}
                  </code>
                </td>
                <td style={tdStyle}>
                  <span style={sourcePillStyles[merged.Source]} data-testid={`source-pill-${key}`}>
                    {merged.Source}
                  </span>
                </td>
                <td style={tdStyle}>
                  {merged.OrgLocked ? (
                    <span
                      title={ORG_LOCKED_TOOLTIP}
                      aria-label={`${key} is org-locked`}
                      data-testid={`locked-${key}`}
                      style={{ fontSize: 14 }}
                    >
                      🔒
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      )}
    </section>
  );
}

const SETTINGS_TABS = [
  { id: 'preferences', label: 'Preferences' },
  { id: 'navigation', label: 'Bookmarks & hyperlinks' },
  { id: 'assistant', label: 'AI assistant' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'advanced', label: 'Advanced' },
] as const;

type SettingsTabId = (typeof SETTINGS_TABS)[number]['id'];

// Keep panels mounted so switching tabs never discards unsaved policy/catalogue/preferences edits.
function SettingsTabPanel({ id, activeTab, children }: {
  readonly id: SettingsTabId;
  readonly activeTab: SettingsTabId;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div role="tabpanel" id={`settings-category-panel-${id}`} aria-labelledby={`settings-category-tab-${id}`} hidden={activeTab !== id} tabIndex={0} style={{ minWidth: 0 }}>
      {children}
    </div>
  );
}

export function SettingsPage(): ReactElement {
  const perms = useUserPermissions();
  // Auth-only writes (user scope) still need to wait for the /me load
  // before we know whether to show buttons — otherwise tests see a
  // pre-load flicker. Treat `loading` as "no, you can't write yet".
  const permsReady = !perms.loading;
  const canWriteOrg = permsReady && perms.hasPermission('Users', 'manage');
  const canWriteUser = permsReady;
  const canWriteSubmission = permsReady && perms.hasPermission('Submissions', 'write');
  // T16-D6 — per-section gate. The page stays open to every signed-in user
  // (the schema, user and notification sections are theirs), but the
  // assistant model catalogue's GET requires AuditLog:read and its PUT
  // Users:manage. The panel renders only with the read permission, so no
  // section calls an endpoint that would 403 for this user.
  const canReadModelCatalog = permsReady && perms.hasPermission('AuditLog', 'read');
  const canEditModelCatalog = canReadModelCatalog && canWriteOrg;

  const [orgState, setOrgState] = useState<ScopeSectionState>(emptyScopeState);
  const [userState, setUserState] = useState<ScopeSectionState>(emptyScopeState);
  const [submissionState, setSubmissionState] = useState<ScopeSectionState>(emptyScopeState);
  const [submissionIdInput, setSubmissionIdInput] = useState('');
  const [loadedSubmissionId, setLoadedSubmissionId] = useState<string | null>(null);
  // phase-settings-inherit-submission-context — the loaded submission's
  // human label ("Drug · Seq NNNN", the assistant panel's model) + the
  // resolution state, so the section never shows a bare UUID as the only
  // identifier and a bad id gets an honest inline error.
  const [submissionLabel, setSubmissionLabel] = useState<string | null>(null);
  const [submissionLabelError, setSubmissionLabelError] = useState<string | null>(null);
  // phase-settings-inherit-submission-context — the submission context is
  // carried in the URL (?submissionId=<uuid>), so it survives refresh and is
  // shareable. The Submission Details ⚙ Settings link produces exactly this
  // param (SubmissionDetailPage: `?scope=submission&submissionId=<id>`).
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSubmissionId = searchParams.get('submissionId');
  const requestedTab = searchParams.get('section');
  const activeTab: SettingsTabId = SETTINGS_TABS.find((tab) => tab.id === requestedTab)?.id
    ?? (searchParams.has('scope') || urlSubmissionId ? 'advanced' : 'preferences');
  const selectTab = useCallback((tab: SettingsTabId): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('section', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [pendingScrollKey, setPendingScrollKey] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingScrollKey) return;
    const row = globalThis.document?.querySelector(`[data-testid="setting-row-${pendingScrollKey}"]`);
    row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setPendingScrollKey(null);
  }, [activeTab, pendingScrollKey]);


  const [editModal, setEditModal] = useState<EditModalTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const { toast, showToast, clearToast } = useUiToast();

  // Phase 5.20c — Effective view tab. Defaults to 'raw' so existing users
  // see the per-scope sections on first navigation. The effective fetch
  // is lazy (only fires when the tab is first selected) and cached
  // across raw↔effective toggles unless the loaded submission id changes
  // or the user explicitly retries.
  const [view, setView] = useState<SettingsView>('raw');
  const [effectiveState, setEffectiveState] = useState<EffectiveState>(emptyEffectiveState);

  // ----- Phase 5.X-settings-schema-ui — schema-UI state -----
  //
  // EAGER load on mount (vs. the existing Effective view tab which is
  // lazy). The schema UI is the primary affordance now; it should always
  // reflect current effective values without waiting for a tab click.
  const [schemaEffective, setSchemaEffective] = useState<
    Record<string, EffectiveValueInfo>
  >({});
  const [schemaReloadKey, setSchemaReloadKey] = useState(0);
  const [schemaSaving, setSchemaSaving] = useState<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    v2.settings
      .listAll()
      .then((res) => {
        if (cancelled) return;
        // Convert MergedSettingsMap → schema-shaped Record<key, {value, scope}>.
        // Non-schema keys are ignored — the generic editor still surfaces them.
        const next: Record<string, EffectiveValueInfo> = {};
        for (const [k, m] of Object.entries(res.settings)) {
          // The 3-tier merge `Source` is already 'org' / 'user' / 'submission'.
          // We just need to coerce Value to boolean | number (matching the
          // schema's defaultValue union). Schema-unknown shapes fall through
          // via Boolean(v) for safety.
          const raw = m.Value;
          let coerced: boolean | number;
          if (typeof raw === 'boolean' || typeof raw === 'number') {
            coerced = raw;
          } else if (typeof raw === 'string') {
            // Stored strings: try numeric coerce, else truthy boolean.
            const n = Number(raw);
            coerced = Number.isFinite(n) && raw.trim() !== '' ? n : Boolean(raw);
          } else {
            coerced = Boolean(raw);
          }
          next[k] = { value: coerced, scope: m.Source as EffectiveScope };
        }
        setSchemaEffective(next);
      })
      .catch(() => {
        // Schema UI degrades silently to defaults; the existing tabs
        // surface the underlying error.
      });
    return (): void => {
      cancelled = true;
    };
  }, [schemaReloadKey]);

  // Combined per-key view: effective value (if any) + schema default fallback.
  const schemaValues = useMemo<Record<string, boolean | number | string>>(() => {
    const out: Record<string, boolean | number | string> = {};
    for (const entry of SETTINGS_SCHEMA) {
      const eff = schemaEffective[entry.key];
      out[entry.key] = eff !== undefined ? eff.value : entry.defaultValue;
    }
    return out;
  }, [schemaEffective]);

  const sectionGroups = useMemo(() => groupSchemaBySection(), []);

  const handleSchemaChange = useCallback(
    async (key: string, next: boolean | number | string): Promise<void> => {
      const entry = SETTINGS_SCHEMA.find((e) => e.key === key);
      if (!entry) return;
      // Optimistic update — flip UI immediately, revert on error.
      const prevInfo = schemaEffective[key];
      setSchemaEffective((cur) => ({
        ...cur,
        [key]: { value: next, scope: entry.level },
      }));
      setSchemaSaving((cur) => new Set(cur).add(key));
      try {
        const body: SettingsWriteBody = { key, value: next };
        if (entry.level === 'org') {
          await v2.settings.updateOrg(body);
        } else {
          await v2.settings.updateUser(body);
        }
        showToast({ type: 'success', text: `Setting "${entry.label}" saved.` });
        // Refetch to pick up authoritative scope ('user' vs. 'org' vs.
        // post-merge 'org' if the user write is shadowed by org-lock).
        setSchemaReloadKey((k) => k + 1);
      } catch (err) {
        // Revert optimistic update.
        setSchemaEffective((cur) => {
          const restored = { ...cur };
          if (prevInfo === undefined) {
            delete restored[key];
          } else {
            restored[key] = prevInfo;
          }
          return restored;
        });
        const msg =
          err instanceof ApiError
            ? (parseApiErrorBody(err)?.message ?? err.message)
            : err instanceof Error
              ? err.message
              : 'Save failed';
        showToast({ type: 'error', text: `Could not save "${entry.label}": ${msg}` });
      } finally {
        setSchemaSaving((cur) => {
          const n = new Set(cur);
          n.delete(key);
          return n;
        });
      }
    },
    [schemaEffective, showToast],
  );

  const scrollToSchemaRow = useCallback((key: string): void => {
    const section = SETTINGS_SCHEMA.find((entry) => entry.key === key)?.section;
    selectTab(section === 'Hyperlinking' ? 'navigation' : section === 'Assistant' ? 'assistant' : 'preferences');
    setPendingScrollKey(key);
  }, [selectTab]);

  // Org section — load on mount and on retry.
  useEffect(() => {
    let cancelled = false;
    setOrgState((prev) => ({ ...prev, rows: null, error: null }));
    v2.settings
      .listOrg()
      .then((res) => {
        if (cancelled) return;
        setOrgState((prev) => ({ ...prev, rows: res.rows, error: null }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? (parseApiErrorBody(err)?.message ?? err.message)
            : 'Failed to load org settings';
        setOrgState((prev) => ({ ...prev, rows: null, error: msg }));
      });
    return (): void => {
      cancelled = true;
    };
  }, [orgState.reloadKey]);

  // User section — load on mount and on retry.
  useEffect(() => {
    let cancelled = false;
    setUserState((prev) => ({ ...prev, rows: null, error: null }));
    v2.settings
      .listUser()
      .then((res) => {
        if (cancelled) return;
        setUserState((prev) => ({ ...prev, rows: res.rows, error: null }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? (parseApiErrorBody(err)?.message ?? err.message)
            : 'Failed to load user settings';
        setUserState((prev) => ({ ...prev, rows: null, error: msg }));
      });
    return (): void => {
      cancelled = true;
    };
  }, [userState.reloadKey]);

  // phase-settings-inherit-submission-context — URL → state. On mount and
  // whenever ?submissionId changes (e.g. arriving from Submission Details, or
  // a shared/refreshed link), auto-load that submission with no manual paste
  // or Load click. Trims for safety; only updates state when the id actually
  // differs so a manual switch (which also writes the URL) doesn't loop.
  useEffect(() => {
    const fromUrl = urlSubmissionId?.trim() ?? '';
    if (fromUrl && fromUrl !== loadedSubmissionId) {
      setLoadedSubmissionId(fromUrl);
      setSubmissionIdInput(fromUrl);
    }
  }, [urlSubmissionId, loadedSubmissionId]);

  // phase-settings-inherit-submission-context — resolve the loaded id to a
  // human label ("Drug · Seq NNNN"), the assistant panel's model (AppShell
  // line ~172). A failed lookup is an honest inline error naming the id — the
  // manual input stays usable; we never surface a raw error body or a bare
  // UUID as the only identifier.
  useEffect(() => {
    if (!loadedSubmissionId) {
      setSubmissionLabel(null);
      setSubmissionLabelError(null);
      return;
    }
    let cancelled = false;
    setSubmissionLabel(null);
    setSubmissionLabelError(null);
    v2.submissions
      .get(loadedSubmissionId)
      .then((sub) => {
        if (cancelled) return;
        setSubmissionLabel(
          `${sub.DrugName ?? 'Submission'} · Seq ${sub.SequenceNumber ?? '?'}`,
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const notFound = err instanceof ApiError && err.status === 404;
        setSubmissionLabelError(
          notFound
            ? `No submission found for ID ${loadedSubmissionId}. Check the ID or load a different one.`
            : `Couldn't load submission ${loadedSubmissionId}. Try again or load a different one.`,
        );
      });
    return (): void => {
      cancelled = true;
    };
  }, [loadedSubmissionId]);

  // Submission section — only fetches once `loadedSubmissionId` is set.
  useEffect(() => {
    if (!loadedSubmissionId) return;
    let cancelled = false;
    setSubmissionState((prev) => ({ ...prev, rows: null, error: null }));
    v2.settings
      .listSubmission(loadedSubmissionId)
      .then((res) => {
        if (cancelled) return;
        setSubmissionState((prev) => ({ ...prev, rows: res.rows, error: null }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? (parseApiErrorBody(err)?.message ?? err.message)
            : 'Failed to load submission settings';
        setSubmissionState((prev) => ({ ...prev, rows: null, error: msg }));
      });
    return (): void => {
      cancelled = true;
    };
  }, [loadedSubmissionId, submissionState.reloadKey]);

  const handleSubmissionLoad = useCallback((e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const trimmed = submissionIdInput.trim();
    if (!trimmed) return;
    setLoadedSubmissionId(trimmed);
    // phase-settings-inherit-submission-context — a manual switch writes the
    // URL too, so the loaded submission is shareable and survives refresh
    // (the URL→state effect is a no-op when they already agree). Preserve the
    // scope param the section routes on; replace history so Back isn't spammed.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('scope', 'submission');
        next.set('submissionId', trimmed);
        return next;
      },
      { replace: true },
    );
  }, [submissionIdInput, setSearchParams]);

  const retryOrg = useCallback(
    () => setOrgState((prev) => ({ ...prev, reloadKey: prev.reloadKey + 1 })),
    [],
  );
  const retryUser = useCallback(
    () => setUserState((prev) => ({ ...prev, reloadKey: prev.reloadKey + 1 })),
    [],
  );
  const retrySubmission = useCallback(
    () => setSubmissionState((prev) => ({ ...prev, reloadKey: prev.reloadKey + 1 })),
    [],
  );

  // Phase 5.20c — fetch the merged effective view.
  //
  // De-duped via `fetchedKeyRef` (NOT a state value) so the effect's
  // internal `setEffectiveState({rows: null, ...})` reset doesn't
  // re-trigger itself. The deps list intentionally excludes
  // `effectiveState.*` for the same reason.
  //
  // Picks listAll() when no submission is loaded, getSubmission(id) otherwise.
  // Staying on 'raw' never refetches.
  const fetchedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (view !== 'effective') return;
    const fetchKey = `${loadedSubmissionId ?? '__listAll__'}#${effectiveState.reloadKey}`;
    if (fetchedKeyRef.current === fetchKey) return;
    fetchedKeyRef.current = fetchKey;

    let cancelled = false;
    setEffectiveState((prev) => ({
      ...prev,
      rows: null,
      error: null,
      fetchedForSubmissionId: loadedSubmissionId,
    }));
    const promise = loadedSubmissionId
      ? v2.settings.getSubmission(loadedSubmissionId)
      : v2.settings.listAll();
    promise
      .then((res) => {
        if (cancelled) return;
        setEffectiveState((prev) => ({ ...prev, rows: res.settings, error: null }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? (parseApiErrorBody(err)?.message ?? err.message)
            : 'Failed to load effective settings';
        setEffectiveState((prev) => ({ ...prev, rows: null, error: msg }));
      });
    return (): void => {
      cancelled = true;
    };
  }, [view, loadedSubmissionId, effectiveState.reloadKey]);

  const retryEffective = useCallback(
    () =>
      setEffectiveState((prev) => ({
        ...prev,
        rows: null,
        error: null,
        reloadKey: prev.reloadKey + 1,
      })),
    [],
  );

  // Set of org keys that are locked — used to disable Edit/Delete on
  // the matching user-scope rows.
  const lockedOrgKeys = useMemo<ReadonlySet<string>>(() => {
    const set = new Set<string>();
    for (const r of orgState.rows ?? []) {
      if (r.OrgLocked) set.add(r.Key);
    }
    return set;
  }, [orgState.rows]);

  const handleDeleteConfirmed = async (target: DeleteTarget): Promise<void> => {
    setDeleteTarget(null);
    try {
      if (target.scope === 'org') {
        await v2.settings.deleteOrg(target.key);
        retryOrg();
      } else if (target.scope === 'user') {
        await v2.settings.deleteUser(target.key);
        retryUser();
      } else {
        if (!loadedSubmissionId) throw new Error('No loaded submission');
        await v2.settings.deleteSubmission(loadedSubmissionId, target.key);
        retrySubmission();
      }
      showToast({ type: 'success', text: `Setting "${target.key}" deleted.` });
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? (parseApiErrorBody(err)?.message ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Delete failed';
      showToast({ type: 'error', text: msg });
    }
  };

  const submissionControlSlot = (
    <form
      onSubmit={handleSubmissionLoad}
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}
    >
      <label style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        Submission ID
        <input
          type="text"
          value={submissionIdInput}
          onChange={(e) => setSubmissionIdInput(e.target.value)}
          placeholder="UUID — paste from the submission detail URL"
          aria-label="Submission ID"
          style={submissionIdInputStyle}
        />
      </label>
      <button
        type="submit"
        disabled={!submissionIdInput.trim()}
        style={{
          ...loadButtonStyle,
          opacity: submissionIdInput.trim() ? 1 : 0.5,
          cursor: submissionIdInput.trim() ? 'pointer' : 'not-allowed',
        }}
      >
        Load
      </button>
      {/* phase-settings-inherit-submission-context — identify the loaded
          submission by its human label, not a bare UUID. While the label
          resolves, fall back to the id; a failed lookup shows the honest
          inline error and the manual input above stays usable. */}
      {loadedSubmissionId && !submissionLabelError && (
        <span
          style={{ fontSize: 12, color: 'var(--text-muted)' }}
          data-testid="settings-loaded-submission-label"
        >
          Showing settings for{' '}
          {submissionLabel ? (
            <strong style={{ color: 'var(--text)' }}>{submissionLabel}</strong>
          ) : (
            <code>{loadedSubmissionId}</code>
          )}
        </span>
      )}
      {submissionLabelError && (
        <span
          role="alert"
          data-testid="settings-submission-error"
          style={{ fontSize: 12, color: 'var(--danger, #b91c1c)' }}
        >
          {submissionLabelError}
        </span>
      )}
    </form>
  );

  // The submission section has its own empty/instruction state when no ID
  // has been entered yet — it's NOT the same as "API returned []".
  const submissionStateForRender: ScopeSectionState = loadedSubmissionId
    ? submissionState
    : { rows: [], error: null, reloadKey: 0 };
  const submissionEmptyText = loadedSubmissionId
    ? 'No submission-level overrides for this submission'
    : 'Enter a submission ID above to view its settings';

  // Submission scope writes only make sense once an ID is loaded; without
  // one there's no target. Hide the "+ New" button until then.
  const canWriteSubmissionNow = canWriteSubmission && !!loadedSubmissionId;

  return (
    <section style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 20, minWidth: 0 }}>
      <header>
        {/* phase-v1-ui-parity — V1 SettingsScreen titles this "My Settings"
            via .screen-title. Subtitle is a V2-only addition kept for clarity. */}
        <h2 className="screen-title" style={{ margin: 0 }}>My Settings</h2>
        <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)' }}>
          Org-level, user-level, and submission-level configuration.
        </p>
      </header>

      <div role="tablist" aria-label="Settings categories" onKeyDown={tabKeyboard} style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {SETTINGS_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`settings-category-tab-${tab.id}`}
            aria-controls={`settings-category-panel-${tab.id}`}
            aria-selected={activeTab === tab.id}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => selectTab(tab.id)}
            style={activeTab === tab.id ? tabButtonActiveStyle : tabButtonStyle}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {schemaSaving.size > 0 && (
        <div data-testid="schema-saving-indicator" style={settingsDescriptionStyle} aria-live="polite">
          Saving {schemaSaving.size} change{schemaSaving.size === 1 ? '' : 's'}…
        </div>
      )}

      <SettingsTabPanel id="preferences" activeTab={activeTab}>
      {/* Keep the collapsible summary above the full-width preference editors. */}
      <div
        data-testid="settings-schema-ui"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)',
          gap: 20,
        }}
      >
        <CurrentConfigurationPanel
          effective={schemaEffective}
          onRowClick={scrollToSchemaRow}
        />
        <div style={{ minWidth: 0, display: 'grid', gap: 20 }}>
          {SECTION_ORDER.filter((section) => section !== 'Hyperlinking' && section !== 'Assistant').map((section) => {
            const entries = sectionGroups[section];
            if (!entries || entries.length === 0) return null;
            return (
              <SettingsSectionPanel
                key={section}
                section={section}
                entries={entries}
                values={schemaValues}
                canWriteOrg={canWriteOrg}
                canWriteUser={canWriteUser}
                onChange={(key, next) => void handleSchemaChange(key, next)}
              />
            );
          })}

        </div>
      </div>

      </SettingsTabPanel>

      <SettingsTabPanel id="navigation" activeTab={activeTab}>
      <div data-testid="navigation-policy-section">
        <NavigationPolicyPanel linkPreferences={
          <div data-testid="settings-section-Hyperlinking">
            <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600 }}>Personal link appearance</h4>
            <p style={{ ...settingsDescriptionStyle, marginBottom: 12 }}>
              These preferences save automatically. Organisation policy changes use Save navigation policy.
            </p>
            <SettingsSectionFields
              entries={sectionGroups.Hyperlinking ?? []}
              values={schemaValues}
              canWriteOrg={canWriteOrg}
              canWriteUser={canWriteUser}
              onChange={(key, next) => void handleSchemaChange(key, next)}
            />
          </div>
        } />
      </div>

      </SettingsTabPanel>

      <SettingsTabPanel id="assistant" activeTab={activeTab}>
        <div style={{ display: 'grid', gap: 20 }}>
          <SettingsSectionPanel
            section="Assistant"
            entries={sectionGroups.Assistant ?? []}
            values={schemaValues}
            canWriteOrg={canWriteOrg}
            canWriteUser={canWriteUser}
            onChange={(key, next) => void handleSchemaChange(key, next)}
          />
          {canReadModelCatalog && <AssistantModelCatalogPanel canEdit={canEditModelCatalog} />}
        </div>
      </SettingsTabPanel>

      <SettingsTabPanel id="notifications" activeTab={activeTab}>
        <div data-testid="notification-preferences-section">
          <NotificationPreferencesPanel />
        </div>
      </SettingsTabPanel>

      <SettingsTabPanel id="advanced" activeTab={activeTab}>
      <details data-testid="advanced-settings-details" open style={settingsCardStyle}>
        <summary style={{ ...settingsHeadingStyle, cursor: 'pointer', userSelect: 'none' }}>
          Advanced settings (raw rows + effective view)
        </summary>
        <p style={settingsDescriptionStyle}>Inspect and manage organisation, personal, and submission overrides.</p>
        <div style={{ marginTop: 12 }}>
        {/* Phase 5.20c — Raw rows vs. Effective view tab toggle. */}
        <div
          role="tablist" onKeyDown={tabKeyboard}
          aria-label="Settings view"
          style={{ display: 'flex', gap: 8, marginBottom: 20 }}
        >
          <button
            type="button"
            role="tab"
            id="settings-view-tab-raw"
            aria-controls="settings-view-tabpanel-raw"
            aria-selected={view === 'raw'} tabIndex={(view === 'raw') ? 0 : -1}
            onClick={() => setView('raw')}
            style={view === 'raw' ? tabButtonActiveStyle : tabButtonStyle}
          >
            Raw rows
          </button>
          <button
            type="button"
            role="tab"
            id="settings-view-tab-effective"
            aria-controls="settings-view-tabpanel-effective"
            aria-selected={view === 'effective'} tabIndex={(view === 'effective') ? 0 : -1}
            onClick={() => setView('effective')}
            style={view === 'effective' ? tabButtonActiveStyle : tabButtonStyle}
          >
            Effective view
          </button>
        </div>

        {view === 'effective' ? (
          <div
            role="tabpanel"
            id="settings-view-tabpanel-effective"
            aria-labelledby="settings-view-tab-effective"
          >
            <EffectiveView
              state={effectiveState}
              loadedSubmissionId={loadedSubmissionId}
              onRetry={retryEffective}
              showToast={showToast}
            />
          </div>
        ) : (
          <div
            role="tabpanel"
            id="settings-view-tabpanel-raw"
            aria-labelledby="settings-view-tab-raw"
            style={{ display: 'grid', gap: 20 }}
          >
        <ScopeSection
          heading="Org settings"
          subtext="Organization-wide defaults. Set by admins; can be locked to prevent user/submission overrides."
          state={orgState}
          emptyText="No org-level settings configured"
          emptyIcon="🏢"
          onRetry={retryOrg}
          canWrite={canWriteOrg}
          onNew={() => setEditModal({ scope: 'org', setting: null })}
          onEdit={(row) => setEditModal({ scope: 'org', setting: row })}
          onDelete={(row) => setDeleteTarget({ scope: 'org', key: row.Key })}
        />

        <ScopeSection
          heading="My user settings"
          subtext="Your personal overrides of org defaults. Other users' settings are not visible here."
          state={userState}
          emptyText="No user-level overrides on file"
          emptyIcon="👤"
          onRetry={retryUser}
          canWrite={canWriteUser}
          onNew={() => setEditModal({ scope: 'user', setting: null })}
          onEdit={(row) => setEditModal({ scope: 'user', setting: row })}
          onDelete={(row) => setDeleteTarget({ scope: 'user', key: row.Key })}
          lockedOrgKeys={lockedOrgKeys}
        />

        <ScopeSection
          heading="Submission settings"
          subtext="Per-submission overrides. Requires Submissions:read on the target submission."
          state={submissionStateForRender}
          emptyText={submissionEmptyText}
          emptyIcon="📄"
          onRetry={retrySubmission}
          controlSlot={submissionControlSlot}
          canWrite={canWriteSubmissionNow}
          onNew={() => setEditModal({ scope: 'submission', setting: null })}
          onEdit={(row) => setEditModal({ scope: 'submission', setting: row })}
          onDelete={(row) => setDeleteTarget({ scope: 'submission', key: row.Key })}
        />
          </div>
        )}
        </div>
      </details>
      </SettingsTabPanel>

      {editModal && (
        <SettingEditModal
          scope={editModal.scope}
          setting={editModal.setting}
          submissionId={loadedSubmissionId ?? undefined}
          onClose={() => setEditModal(null)}
          onSaved={(saved) => {
            const wasCreate = editModal.setting === null;
            const scope = editModal.scope;
            setEditModal(null);
            if (scope === 'org') retryOrg();
            else if (scope === 'user') retryUser();
            else retrySubmission();
            showToast({
              type: 'success',
              text: wasCreate
                ? `Setting "${saved.Key}" created.`
                : `Setting "${saved.Key}" updated.`,
            });
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete setting"
          message={`Permanently delete "${deleteTarget.key}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirmed(deleteTarget)}
        />
      )}

      <PageToast toast={toast} onDismiss={clearToast} />
    </section>
  );
}
